import apiClient from './client';
import type {
  Session,
  SessionList,
  SessionResults,
  SessionResumeData,
  CreateSessionPayload,
  CursorUpdatePayload,
  SubmitAnswerPayload,
  SubmitAnswerResponse,
  ExamStateUpdatePayload,
  ActiveExam,
  RevealRequest,
  RevealResponse,
} from '../types';

/** Create a new study or exam session. */
export async function createSession(
  payload: CreateSessionPayload,
  certification?: string,
): Promise<Session> {
  const body = certification ? { ...payload, certification_code: certification } : payload;
  const { data } = await apiClient.post<Session>('/api/sessions', body);
  return data;
}

/** Fetch a session by ID. */
export async function fetchSession(id: number): Promise<Session> {
  const { data } = await apiClient.get<Session>(`/api/sessions/${id}`);
  return data;
}

/** Submit an answer for a question within a session. */
export async function submitAnswer(
  sessionId: number,
  payload: SubmitAnswerPayload,
): Promise<SubmitAnswerResponse> {
  const { data } = await apiClient.post<SubmitAnswerResponse>(
    `/api/sessions/${sessionId}/answer`,
    payload,
  );
  return data;
}

/**
 * Reveal a question's correct answer + explanation during an exam session
 * ("review mode"). Read-only: does not persist an answer or affect the score.
 */
export async function revealAnswer(
  sessionId: number,
  payload: RevealRequest,
): Promise<RevealResponse> {
  const { data } = await apiClient.post<RevealResponse>(
    `/api/sessions/${sessionId}/reveal`,
    payload,
  );
  return data;
}

/** Fetch results for a completed session. */
export async function fetchResults(sessionId: number): Promise<SessionResults> {
  const { data } = await apiClient.get<SessionResults>(
    `/api/sessions/${sessionId}/results`,
  );
  return data;
}

/** List the current user's sessions. */
export async function fetchMySessions(
  params?: {
    mode?: string;
    active_only?: boolean;
    status?: 'active' | 'completed' | 'all';
    limit?: number;
    offset?: number;
  },
  certification?: string,
): Promise<SessionList> {
  const { data } = await apiClient.get<SessionList>('/api/sessions/my', {
    params: { ...params, ...(certification ? { certification } : {}) },
  });
  return data;
}

/** Save cursor position for a session. */
export async function updateCursor(
  sessionId: number,
  payload: CursorUpdatePayload,
): Promise<void> {
  await apiClient.patch(`/api/sessions/${sessionId}/cursor`, payload);
}

/** Get full session state for resuming. */
export async function fetchSessionResume(sessionId: number): Promise<SessionResumeData> {
  const { data } = await apiClient.get<SessionResumeData>(
    `/api/sessions/${sessionId}/resume`,
  );
  return data;
}

/** Mark a session as completed. */
export async function completeSession(sessionId: number): Promise<void> {
  await apiClient.patch(`/api/sessions/${sessionId}/complete`);
}

/** Delete a session. */
export async function deleteSession(sessionId: number): Promise<void> {
  await apiClient.delete(`/api/sessions/${sessionId}`);
}

/** Rename a session. */
export async function renameSession(sessionId: number, name: string): Promise<void> {
  await apiClient.patch(`/api/sessions/${sessionId}/rename`, { session_name: name });
}

/** Get the user's active (in-progress) exam session, if any. */
export async function fetchActiveExam(certification?: string): Promise<ActiveExam | null> {
  const { data } = await apiClient.get<ActiveExam | null>('/api/sessions/active-exam', {
    params: certification ? { certification } : undefined,
  });
  return data;
}

/** Save exam state (question order, current index, time remaining) without submitting an answer. */
export async function saveExamState(
  sessionId: number,
  payload: ExamStateUpdatePayload,
): Promise<void> {
  await apiClient.patch(`/api/sessions/${sessionId}/exam-state`, payload);
}

/** Download a session certificate as a PDF blob. */
export async function downloadCertificate(sessionId: number): Promise<Blob> {
  const { data } = await apiClient.get(`/api/sessions/${sessionId}/certificate`, {
    responseType: 'blob',
  });
  return data;
}
